import React, { type ReactNode, createContext, useContext, useEffect, useMemo, useRef } from 'react';

import { BarChart, BoxplotChart, HeatmapChart, LineChart } from 'echarts/charts';
import { CalendarComponent, VisualMapComponent, GridComponent, LegendComponent, MarkAreaComponent, MarkLineComponent, MarkPointComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { LabelLayout } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';

import { SeriesFocusContext, focusOption, seriesOf } from '../seriesFocus';
import useChartZoom, { type ChartZoom } from './useChartZoom';

echarts.use([CalendarComponent, VisualMapComponent, HeatmapChart, BarChart, BoxplotChart, HeatmapChart, LineChart, GridComponent, LegendComponent, MarkAreaComponent, MarkLineComponent, MarkPointComponent, TooltipComponent, LabelLayout, SVGRenderer]);

export type ChartOption = echarts.EChartsCoreOption;

const NAMED_SCALE: Record<string, number> = { small: 0.9, medium: 1, large: 1.15 };

const STANDARD_TEXT_PX = 13;

export const textScaleOf = (size: unknown): number | undefined => (typeof size === 'number' && size >= 9 && size <= 24 ? size / STANDARD_TEXT_PX : typeof size === 'string' ? NAMED_SCALE[size] : undefined);

export const TextScaleContext = createContext(1);

function scaledFonts(value: unknown, scale: number): unknown {
  if (Array.isArray(value)) return value.length && typeof value[0] !== 'object' ? value : value.map((entry) => scaledFonts(entry, scale));
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, key === 'fontSize' && typeof entry === 'number' ? Math.round(entry * scale * 2) / 2 : scaledFonts(entry, scale)]));
}

function recordAxisSpan(instance: echarts.ECharts, host: HTMLDivElement) {
  const axis = (instance.getOption().xAxis as Array<{ type?: string; data?: Array<unknown> }> | undefined)?.[0];
  const count = axis?.data?.length ?? 0;
  if (axis?.type !== 'category' || count < 2 || !host.clientWidth) {
    delete host.dataset.lensAxisSpan;
    return;
  }
  const first = instance.convertToPixel({ xAxisIndex: 0 }, 0) as number;
  const last = instance.convertToPixel({ xAxisIndex: 0 }, count - 1) as number;
  host.dataset.lensAxisSpan = (Math.abs(last - first) / host.clientWidth).toFixed(3);
}

export default function Chart({ option, className = 'chart', zoom, overlay, onItem }: { option: ChartOption; className?: string; zoom?: ChartZoom; overlay?: ReactNode; onItem?: (name: string) => void }) {
  const host = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLDivElement>(null);
  const chart = useRef<echarts.ECharts | undefined>(undefined);
  const band = useChartZoom(host, chart, zoom);
  const focus = useContext(SeriesFocusContext);
  const focusing = useRef(focus);
  focusing.current = focus;
  const itemClick = useRef(onItem);
  itemClick.current = onItem;
  const scale = useContext(TextScaleContext);
  const shown = useMemo(() => {
    const focused = focusOption(option, focus);
    return scale === 1 ? focused : (scaledFonts(focused, scale) as ChartOption);
  }, [option, focus?.hidden, focus?.hovered, scale]);
  const drawn = useRef<{ option?: ChartOption; hidden?: ReadonlySet<string> }>({});
  useEffect(() => {
    if (!host.current || !canvas.current) return undefined;
    const instance = echarts.init(canvas.current, null, { renderer: 'svg' });
    chart.current = instance;
    instance.on('mouseover', (params: { componentType?: string; seriesName?: string; event?: { target?: { type?: string } } }) => {
      if (params.componentType === 'series' && params.event?.target?.type === 'text') focusing.current?.hover(seriesOf(params.seriesName));
    });
    instance.on('click', (params: { componentType?: string; seriesId?: string; name?: string; event?: { event?: Event } }) => {
      if (itemClick.current && params.componentType === 'series' && params.name) {
        params.event?.event?.preventDefault();
        itemClick.current(params.name);
        return;
      }
      const part = ['yAxis', 'xAxis', 'markLine'].includes(params.componentType ?? '') ? '@axis' : params.componentType === 'series' && params.seriesId ? `series:${params.seriesId.replace(/:tail$/, '')}` : undefined;
      if (part) host.current?.dispatchEvent(new CustomEvent('lens-part', { bubbles: true, detail: part }));
    });
    instance.on('mouseout', (params: { event?: { target?: { type?: string } } }) => {
      if (params.event?.target?.type === 'text') focusing.current?.hover(undefined);
    });
    const observer = new ResizeObserver(() => {
      instance.resize();
      if (host.current) recordAxisSpan(instance, host.current);
    });
    observer.observe(host.current);
    return () => {
      observer.disconnect();
      instance.dispose();
      chart.current = undefined;
    };
  }, []);
  useEffect(() => {
    if (!chart.current || !host.current) return;
    const restyle = drawn.current.option === option && drawn.current.hidden === focus?.hidden;
    chart.current.setOption(shown, { notMerge: !restyle });
    drawn.current = { option, hidden: focus?.hidden };
    recordAxisSpan(chart.current, host.current);
  }, [shown]);
  return (
    <div className={zoom ? `${className} zoomable` : className} data-lens-chart ref={host}>
      <div className="canvas" ref={canvas} />
      {band ? <div className="zoom-band" style={{ left: band.left, width: band.width }} /> : null}
      {overlay}
    </div>
  );
}
